import styled from '@emotion/styled';
import {spacing} from '@nfq/react-grid';
import {observer} from 'mobx-react-lite';
import * as motion from 'motion/react-m';

import {P} from 'UI/components/layout/Text';

import {BodyAnim, IconAnim, WrapperAnim} from 'UI/animations/accordion';

import {useAccordion} from './useAccordion';
import {ChevronDown} from 'Images/icons';

import type {WithChildren} from 'types/global';

/**
 * The props for the `Accordion` component, which renders a collapsible section with a header and body.
 * This interface defines the structure for configuring the accordion's header label,
 * unique identification, and testing attributes.
 */
interface ComponentProps {
    /**
     * A unique identifier for the accordion, used to link the header button with the collapsible body
     * via ARIA attributes (`aria-controls`, `aria-labelledby`). Ensures accessibility and uniqueness
     * when multiple accordions are present on a page.
     */
    accordionId?: string | null;
    /**
     * The header text displayed inside the accordion toggle button.
     * If omitted, the header will render as an empty string.
     */
    head?: string | null;
    /**
     * The `testId` property represents a unique identifier, usually in the form of a string, assigned to a component for testing purposes.
     * This property is crucial for uniquely identifying components during testing, allowing for more accurate and reliable tests.
     *
     * @default 'Accordion'
     */
    testId?: string;
}

/**
 * A collapsible accordion component with animated open/close transitions.
 * This component renders a header with a toggle button and a body section that expands
 * or collapses when clicked. It uses the `useAccordion` hook to manage the open/close
 * state and applies framer-motion variants (`WrapperAnim`, `IconAnim`, `BodyAnim`)
 * to animate the transitions. Accessibility attributes such as `aria-controls`,
 * `aria-expanded`, and `aria-labelledby` are included to ensure screen reader support.
 * The `accordionId` prop links the header and body elements, ensuring unique identification
 * when multiple accordions are rendered on the same page. Children passed into the component
 * are displayed inside the collapsible body section.
 *
 * @param props             The component props.
 * @param props.accordionId A unique identifier for the accordion, used to link header and body via ARIA attributes.
 * @param props.children    The content displayed inside the accordion body when expanded.
 * @param props.head        The header text displayed in the accordion toggle button.
 * @param props.testId      An optional identifier used for testing, applied as `data-cy`. Defaults to `'Accordion'`.
 * @returns A JSX element rendering the accordion with animated toggle behavior.
 *
 * @example
 * ```tsx
 * <Accordion accordionId="faq-1" head="What is your return policy?">
 *   <p>You can return items within 30 days of purchase with a receipt.</p>
 * </Accordion>
 * ```
 */
const Accordion = observer(({accordionId, children, head, testId = 'Accordion'}: WithChildren<ComponentProps>) => {
    const {handleToggle, isOpen} = useAccordion();

    return (
        <Wrapper animate={isOpen ? 'open' : 'closed'} data-cy={testId} initial="initial" variants={WrapperAnim}>
            <Head $size="medium" as="h3">
                <AccButton
                    aria-controls={accordionId ?? undefined}
                    aria-expanded={isOpen}
                    id={`${accordionId}-header`}
                    type="button"
                    onClick={handleToggle}
                >
                    <AnimIcon height={24} variants={IconAnim} width={24} />
                    {head ?? ''}
                </AccButton>
            </Head>
            <Body aria-labelledby={`${accordionId}-header`} id={accordionId ?? undefined} role="region" variants={BodyAnim}>
                <BodyInner>
                    {children}
                </BodyInner>
            </Body>
        </Wrapper>
    );
});

Accordion.displayName = 'Accordion';

export {Accordion};

const Wrapper = styled(motion.div)`
    border-bottom: 1px solid ${({theme}) => theme.colors.neutral300};
`;

const Head = styled(P)``;

const AccButton = styled.button`
    align-items: center;
    appearance: none;
    background: none;
    border: none;
    color: ${({theme}) => theme.colors.textPrimary};
    cursor: pointer;
    display: flex;
    font-family: inherit;
    font-size: inherit;
    font-weight: inherit;
    gap: ${spacing(4)};
    line-height: inherit;
    padding-block: ${spacing(8)};
    padding-inline: 0;
    text-align: left;
    width: 100%;

    &:focus-visible {
        outline-color: ${({theme}) => theme.colors.focus};
        outline-offset: 4px;
        outline-style: solid;
        outline-width: 4px;
    }
`;

const AnimIcon = styled(motion.create(ChevronDown))`
    flex: 0 0 24px;
`;

const Body = styled(motion.div)`
    height: 0px;
    overflow: hidden;
`;

const BodyInner = styled.div`
    padding-block-end: ${spacing(12)};
`;